import React, {useMemo, useState} from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel
} from "@mui/material";

export type ColumnDefinition<T> = {
    id: string,
    accessor: keyof T | ((row: T) => T[keyof T]),
    header?: React.ReactNode,
    render?: (value: T[keyof T], row: T) => React.ReactNode,
    sortBy?: (value: T[keyof T], row: T) => string | number,
    width?: string,
    textAlign?: "left" | "center" | "right",
};

type Order = 'asc' | 'desc';

const access = <T,>(col: ColumnDefinition<T>, row: T): T[keyof T] => {
    const {accessor} = col;
    return typeof accessor === 'function' ? accessor(row) : row[accessor];
}

const renderCell = <T,>(col: ColumnDefinition<T>, row: T) => {
    const {render = x => x as React.ReactNode} = col;
    return render(access(col, row), row);
};

const compare = <T,>(a: T, b: T, orderBy: ColumnDefinition<T>) => {
    const sortBy = orderBy?.sortBy ?? (x => String(x));
    const valueA = sortBy(access(orderBy, a), a);
    const valueB = sortBy(access(orderBy, b), b);
    if (valueA < valueB) {
        return -1;
    }
    if (valueA > valueB) {
        return 1;
    }
    return 0;
};

const getComparator = <T,>(order: Order, orderBy: ColumnDefinition<T>) => order === 'asc'
    ? (a: T, b: T) => compare(a, b, orderBy)
    : (a: T, b: T) => -compare(a, b, orderBy);

export const DataTable = <T extends {id: string},>(props: {
    data: T[],
    columns: ColumnDefinition<T>[],
    size?: "medium" | "small",
}) => {
    const {data, columns, size = "medium"} = props;

    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<ColumnDefinition<T> | undefined>(undefined);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const handleChangePage = (_event: React.MouseEvent | null, page: number) => setPage(page);
    const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setPageSize(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    const onRequestSort = (col: ColumnDefinition<T>) => {
        setOrder(orderBy?.id === col.id && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(col);
    };

    const dataPage = useMemo(
        () => (orderBy ? [...data].sort(getComparator(order, orderBy)) : data)
            .slice(page * pageSize, page * pageSize + pageSize),
        [data, page, pageSize, order, orderBy]);

    return (
        <Table size={size}>
            {columns.some(c => c.header) && (
                <TableHead>
                    {columns.map(col => {
                        const isActive = orderBy?.id === col.id;
                        return (
                            <TableCell key={col.id}
                                       align={col.textAlign}
                                       sortDirection={isActive ? order : false}
                                       width={col.width}>
                                <TableSortLabel active={isActive}
                                                direction={order}
                                                onClick={() => onRequestSort(col)}>
                                    {col.header}
                                </TableSortLabel>
                            </TableCell>
                        );
                    })}
                </TableHead>
            )}
            <TableBody>
                {dataPage.map((row) => (
                    <TableRow key={row.id} hover>
                        {columns.map(col => (
                            <TableCell key={col.id} align={col.textAlign} width={col.width}>
                                {renderCell(col, row)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
            {data.length > pageSize &&
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            colSpan={columns.length}
                            count={data.length}
                            rowsPerPage={pageSize}
                            page={page}
                            onPageChange={handleChangePage}
                            showFirstButton
                            showLastButton
                            onRowsPerPageChange={handleChangePageSize}/>
                    </TableRow>
                </TableFooter>}
        </Table>
    );
};
